import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import type { DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlassian/jira-feature-gating';
import { UNASSIGNED_EPIC_ID } from '../../../common/constants/index.tsx';
import type { FieldConfigs } from '../../../common/hooks/use-store-fields/types.tsx';
import { SPRINT } from '../../../model/constants.tsx';
import type { CardId, Card, CardList } from '../../../model/types.tsx';
import cardCreateRest from '../../../rest/card-create-multiple-gql/index.tsx';
import type { NewCardGql, CardCreateGql } from '../../../rest/card-create-multiple-gql/types.tsx';
import {
	getBaseUrl,
	getCloudId,
	getRapidViewId,
	getSprintFieldId,
	getStoryPointFieldId,
	projectIdSelector,
} from '../../../state/selectors/app/index.tsx';
import {
	getMostRecentAssigneeFilter,
	getAppliedLabelFilters,
} from '../../../state/selectors/card-create/index.tsx';
import { getCardLists } from '../../../state/selectors/cards/index.tsx';
import type { MiddlewareAPI } from '../../../state/types.tsx';
import { bulkCreateIssuesWithDescription } from '../../../rest/bulk-create-issue-with-description/index.tsx';
import type { BulkIssueCreationGraphqlResponse } from '../../../rest/bulk-create-issue-with-description/types.tsx';
import type { IssueParent } from '../../../model/issue-parent/types.tsx';
import { issueParentsSelector } from '../../../state/selectors/issue-parent/index.tsx';

const transformParent = (parentId: string, parents: IssueParent[]): IssueParent | null => {
	if (!parentId) return null;
	return parents.find((parent) => parent.id === parentId) ?? null;
};

const transformer = (
	{
		id,
		done: isDone,
		flagged: isFlagged,
		parentId,
		fixVersions,

		issue: {
			key,
			summary,
			type: { id: cardTypeId, iconUrl: cardTypeUrl, name: cardTypeName },
			labels,
			status: { id: statusId },
			assignee,
		},

		priority,
		dueDate,
	}: NewCardGql,
	projectId: number,
	parents: IssueParent[],
): Card => ({
	id: Number(id),
	key,
	summary,
	icon: cardTypeUrl,
	typeId: Number(cardTypeId),
	isDone,
	labels,
	isFlagged,
	assignee: assignee
		? {
				id: assignee.accountId,
				displayName: assignee.displayName,
				avatarUrl: assignee.avatarUrl,
			}
		: null,
	issueUrl: `/browse/${key}`,
	statusId,
	parentId: parentId || '' ? Number(parentId) : null,
	parent: parentId ? transformParent(parentId, parents) : null,
	fixVersions: fixVersions || [],
	priority,
	dueDate,
	estimate: null, // Initial estimate is always NULL for created card
	numCompleteChildren: 0,
	numTotalChildren: 0,
	projectId,
	issueTypeName: cardTypeName,
});

export function transformBulkCreationResponseToTMPCardCreateGql(
	response: BulkIssueCreationGraphqlResponse,
	overrides: Pick<NewCardGql, 'flagged' | 'done' | 'dueDate' | 'fixVersions'>,
): CardCreateGql {
	const newCards = Object.values(response)
		.map((creationRequest) => {
			if (!creationRequest?.createIssue?.issue) return null;

			const { issue } = creationRequest.createIssue;

			const issueGql = {
				id: issue.issueId,
				key: issue.key,
				summary: issue.summary,
				labels:
					issue.fieldSetsById.edges[0]?.node?.fields.edges[0]?.node?.labels?.edges.map(
						(label) => label.node.name,
					) || [],
				type: {
					id: issue.issueTypeField.issueType.issueTypeId,
					name: issue.issueTypeField.issueType.name,
					iconUrl: issue.issueTypeField.issueType.avatar.medium,
				},
				status: {
					id: issue.statusField.status.statusId,
					name: issue.statusField.status.name,
					category: issue.statusCategory.key,
				},
				assignee: issue.assigneeField.user
					? {
							accountId: issue.assigneeField.user.accountId,
							displayName: issue.assigneeField.user.name,
							avatarUrl: issue.assigneeField.user.picture,
						}
					: null,
			};

			const priorityGql = issue.priorityField?.priority
				? {
						name: issue.priorityField.priority.name,
						iconUrl: issue.priorityField.priority.iconUrl,
					}
				: null;

			const newCardGql = {
				id: issue.issueId,
				parentId: issue.parentIssueField.parentIssue?.issueId || null,
				issue: issueGql,
				priority: priorityGql,
				...overrides,
			};
			// newCardGql
			return newCardGql;
		})
		.filter(Boolean);

	// Construct the eventual object
	const cardCreateGql = {
		planModeCardCreate: {
			newCards,
		},
	};

	return cardCreateGql;
}

export const createMultipleCardService = (
	// TODO: Passing the store is against the architecture, use service-context instead and request objects;
	// additionally, don't pass in a callback parameter
	store: MiddlewareAPI,
	summaries: string[],
	issueTypeId: number,
	parentId: number | null,
	fixVersions: string[],
	cardListId: string,
	rankBeforeCardId: CardId | null = null,
	descriptions: (string | DocNode)[] | null,
	storeFieldsFromCardCreateData: (
		cardCreateData: CardCreateGql,
		fieldConfigs: FieldConfigs,
	) => void,
): ObservableType<Card[] | null> => {
	const state = store.getState();
	const baseUrl = getBaseUrl(state);
	const boardId = getRapidViewId(state);
	const storyPointFieldId = getStoryPointFieldId(state);
	const assignee = getMostRecentAssigneeFilter(state);
	const labels = getAppliedLabelFilters(state);
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const cardList = getCardLists(state)[cardListId] as CardList;
	const projectId = projectIdSelector(state);
	const cloudId = getCloudId(state);
	const sprintFieldId = getSprintFieldId(state);
	const isSprintCardList = cardList.type === SPRINT;
	const sprintIds = isSprintCardList ? [cardListId] : [];
	const parents = issueParentsSelector(state);

	if (descriptions && fg('ai_powered_backlog_inline_create_gate')) {
		return (
			bulkCreateIssuesWithDescription({
				projectId: String(projectId),
				cloudId,
				issueTypeId: String(issueTypeId),
				parentId: parentId ? String(parentId) : null,
				fixVersions,
				assigneeId: assignee ? assignee.id : null,
				labels,
				cards: summaries.map((summary, index) => ({
					summary,
					description: descriptions[index],
				})),
				...(fg('ai_powered_backlog_inline_create-create_in_sprint')
					? { sprintFieldId, sprintIds }
					: {}),
			})
				.map((response) =>
					transformBulkCreationResponseToTMPCardCreateGql(response, {
						flagged: false, // Assume false for now
						done: false, // Assume false for now
						dueDate: null, // Assume empty for now
						fixVersions, // `createIssue` endpoint doesn't return fixVersions, assume all association are successful
					}),
				)
				// @ts-expect-error - TS2345 - Argument of type '(result: CardCreateGql) => ObservableType<null> | ObservableType<Card[]>' is not assignable to parameter of type '(value: CardCreateGql, index: number) => ObservableInput<Card[] | null>'.
				.flatMap((result: CardCreateGql) => {
					const fieldConfigs: FieldConfigs = { storyPointFieldId };

					// Pre-populate extracted fields (Sweet State) using card create data
					storeFieldsFromCardCreateData(result, fieldConfigs);
					if (result.planModeCardCreate.newCards && result.planModeCardCreate.newCards.length > 0) {
						const transformedCards = result.planModeCardCreate.newCards.map((card) =>
							transformer(card, projectId, parents),
						);
						return Observable.of(transformedCards);
					}
					return Observable.of(null);
				})
		);
	}

	return cardCreateRest(baseUrl, {
		boardId,
		destination: cardList.type,
		destinationId: cardList.type === SPRINT ? cardList.id : null,
		newCards: summaries.map((summary) => ({
			summary,
			issueTypeId,
			parentId: parentId === UNASSIGNED_EPIC_ID ? null : parentId, // NOTE: The "issues without epic" filter sets parentId to 0 which is mapped back to null
			fixVersions,
			assigneeId: assignee ? assignee.id : null,
			labels: labels || [],
		})),
		rankBeforeCardId,
		// @ts-expect-error - TS2345 - Argument of type '(result: CardCreateGql) => ObservableType<null> | ObservableType<Card[]>' is not assignable to parameter of type '(value: CardCreateGql, index: number) => ObservableInput<Card[] | null>'.
	}).flatMap((result: CardCreateGql) => {
		const fieldConfigs: FieldConfigs = { storyPointFieldId };

		// Pre-populate extracted fields (Sweet State) using card create data
		storeFieldsFromCardCreateData(result, fieldConfigs);
		if (result.planModeCardCreate.newCards && result.planModeCardCreate.newCards.length > 0) {
			const transformedCards = result.planModeCardCreate.newCards.map((card) =>
				transformer(card, projectId, parents),
			);
			return Observable.of(transformedCards);
		}
		return Observable.of(null);
	});
};
